import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  fetchDevice,
  updateDevice,
  retireDevice,
  deleteDevice,
  forceDeleteDevice,
  deviceDeletionImpact,
  fetchLifecycle,
  transitionLifecycle,
  changeDeviceOwner,
  fetchTypeOptions,
  type TypeOption,
} from '../api/devices';
import { ProtocolBadge } from '../components/ProtocolBadge';
import { fetchClients } from '../api/clients';
import { ApiError } from '../api/client';
import { ocppCommand, triggerOta, setRelay } from '../api/maintenance';
import { useAuth } from '../context/AuthContext';
import type { ClientRow, DeviceDetail, Env, LifecycleData, LifecycleStatus } from '../types';
import { DeviceMap, type MapPoint, isValidLatLng } from '../components/DeviceMap';
import {
  LifecycleBadge,
  LIFECYCLE_META,
  LIFECYCLE_ORDER,
  lifecycleLabel,
} from '../components/LifecycleBadge';

export function DeviceDetailPage() {
  const { sn = '' } = useParams();
  const [searchParams] = useSearchParams();
  const envParam = (searchParams.get('env') as Env | null) ?? undefined;
  const navigate = useNavigate();
  const { user } = useAuth();
  const canMaintain = user?.role === 'operator' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const [device, setDevice] = useState<DeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const [otaUrl, setOtaUrl] = useState('');

  // Edit form (strict whitelist: name / manufacturing date / hardware version / ZCE S/N)
  const [editName, setEditName] = useState('');
  const [editMfg, setEditMfg] = useState('');
  const [editHw, setEditHw] = useState('');
  const [editZceSn, setEditZceSn] = useState('');

  // « Nom de client » : libelle libre (migration 008), distinct du Client (proprietaire)
  const [editClientName, setEditClientName] = useState('');

  // « Client » = proprietaire de la borne (devices.device_owner -> users). Le changer est un
  // TRANSFERT REEL : la borne passe dans l'app du nouveau compte (meme operation que le
  // flux natif assign/force_reassign de wattelzce). Liste = clients existants de l'env.
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [editOwner, setEditOwner] = useState('');

  // Type : l'OCPP le recoit de device-service ; un appareil MQTT le saisit ici (013).
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [editType, setEditType] = useState('');

  // Fiche de vie (journal + transitions autorisees)
  const [lifecycle, setLifecycle] = useState<LifecycleData | null>(null);
  const [lcTarget, setLcTarget] = useState<LifecycleStatus | ''>('');
  const [lcReason, setLcReason] = useState('');

  function loadDevice() {
    setLoading(true);
    setError(null);
    fetchDevice(sn, envParam)
      .then((r) => setDevice(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  useEffect(loadDevice, [sn, envParam]);

  // The journal needs the device's resolved env (detail response), not the URL param.
  useEffect(() => {
    if (!device) return;
    fetchLifecycle(sn, device.env)
      .then((r) => setLifecycle(r.data))
      .catch(() => setLifecycle(null));
  }, [sn, device]);

  useEffect(() => {
    if (!device) return;
    setEditName(device.deviceName || '');
    setEditMfg(device.manufacturingDate ? device.manufacturingDate.slice(0, 10) : '');
    setEditHw(device.hardwareVersion || '');
    setEditZceSn(device.zceSerialNumber || '');
    setEditClientName(device.clientName || '');
    setEditOwner(device.ownerUuid || '');
    setEditType(device.deviceTypeId != null ? String(device.deviceTypeId) : '');
  }, [device]);

  // Liste des types, pour le selecteur des appareils MQTT.
  useEffect(() => {
    if (device?.source !== 'mqtt') return;
    fetchTypeOptions(device.env)
      .then((r) => setTypes(r.data))
      .catch(() => setTypes([]));
  }, [device?.source, device?.env]);

  // Clients existants de l'env de la borne (ceux de la colonne « Client » / Vue client).
  useEffect(() => {
    const targetEnv = device?.env;
    if (!targetEnv) return;
    const p = new URLSearchParams({
      env: targetEnv,
      sort: 'displayName',
      dir: 'asc',
      page: '1',
      pageSize: '10000',
    });
    fetchClients(p)
      .then((r) => setClients(r.data))
      .catch(() => setClients([]));
  }, [device?.env]);

  async function run(action: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    setToast(null);
    try {
      await action();
      setToast({ kind: 'ok', msg: okMsg });
    } catch (e) {
      setToast({ kind: 'err', msg: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="t-muted">Chargement...</p>;
  if (error) return <p className="text-red-400">{error}</p>;
  if (!device) return null;

  const env = device.env;
  const writesOn = !!device.writesEnabled;
  // Appareil MQTT sans fiche `devices` : memes ecrans, mais le « Client » est l'organisation
  // du dashboard, le type se saisit, et il n'y a ni commandes OCPP ni retrait/suppression ici.
  const isMqtt = device.source === 'mqtt';

  async function saveEdit() {
    if (!device) return;
    // Changement de Client (device_owner) : seulement si un autre client existant a ete choisi.
    const currentOwner = device.ownerUuid || '';
    const ownerChanged = !isMqtt && !!editOwner && editOwner !== currentOwner;
    if (ownerChanged) {
      const target = clients.find((c) => c.clientUuid === editOwner);
      const from = device.ownerName || device.ownerEmail || 'aucun (stock)';
      const to = target
        ? `${target.displayName}${target.email && target.displayName !== target.email ? ` (${target.email})` : ''}`
        : editOwner;
      const placeNote = device.placeName
        ? `\n\nAttention : la borne est rattachee au lieu « ${device.placeName} », qui n'est pas transfere.`
        : '';
      if (
        !window.confirm(
          `Transferer la borne ${device.deviceSn ?? sn} (${env}) a un autre client ?\n\n` +
            `Actuel : ${from}\nNouveau : ${to}\n\n` +
            `La borne disparait de l'app de l'ancien client et apparait dans celle du nouveau. ` +
            `Action journalisee a votre nom.${placeNote}`
        )
      )
        return;
    }
    setBusy(true);
    setToast(null);
    let fieldsSaved = false;
    try {
      await updateDevice(sn, env, {
        device_name: editName.trim(),
        manufacturing_date: editMfg || null,
        hardware_version: editHw || null,
        zce_serial_number: editZceSn.trim() || null,
        client_name: editClientName.trim() || null,
        ...(isMqtt ? { device_type_id: editType ? Number(editType) : null } : {}),
      });
      fieldsSaved = true;
      if (ownerChanged) {
        const r = await changeDeviceOwner(sn, env, editOwner);
        setToast({
          kind: 'ok',
          msg: `Modifications enregistrees — borne transferee a ${r.data.to.email || 'un autre client'}`,
        });
      } else {
        setToast({ kind: 'ok', msg: 'Modifications enregistrees' });
      }
      loadDevice();
    } catch (e) {
      const msg = (e as Error).message;
      setToast({
        kind: 'err',
        msg: fieldsSaved
          ? `Champs enregistres, mais le changement de client a echoue : ${msg}`
          : msg,
      });
      if (fieldsSaved) loadDevice();
    } finally {
      setBusy(false);
    }
  }

  async function doTransition() {
    if (!lcTarget) return;
    const fromLabel = lifecycleLabel(device?.lifecycleStatus);
    const toLabel = lifecycleLabel(lcTarget);
    if (
      !window.confirm(
        `Changer l'etape de vie : « ${fromLabel} » -> « ${toLabel} » ?\nLe changement sera journalise a votre nom.`
      )
    )
      return;
    setBusy(true);
    setToast(null);
    try {
      await transitionLifecycle(sn, env, lcTarget, lcReason.trim() || undefined);
      setToast({ kind: 'ok', msg: `Etape changee : ${toLabel}` });
      setLcTarget('');
      setLcReason('');
      loadDevice(); // refetches the journal too (effect on device)
    } catch (e) {
      setToast({ kind: 'err', msg: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function doRetire() {
    if (
      !window.confirm(
        "Retirer cette borne du service ? Son statut passe a « inactive » (l'historique est conserve)."
      )
    )
      return;
    setBusy(true);
    setToast(null);
    try {
      await retireDevice(sn, env);
      setToast({ kind: 'ok', msg: 'Borne retiree du service (inactive)' });
      loadDevice();
    } catch (e) {
      setToast({ kind: 'err', msg: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function forceDelete(label: string) {
    let impactText = '(details indisponibles)';
    try {
      const i = (await deviceDeletionImpact(sn, env)).data;
      impactText =
        `- ${i.transactions} transaction(s)\n` +
        `- ${i.chargingTransactions} session(s) de charge (+ facturation liee)\n` +
        `- ${i.connectors} connecteur(s)\n` +
        `- ${i.reservations} reservation(s)\n` +
        `- ${i.meterValues} releve(s) compteur`;
    } catch {
      /* garde le texte par defaut */
    }
    const ok = window.confirm(
      `SUPPRESSION FORCEE de "${label}"\n\nCette borne a un historique. Seront DEFINITIVEMENT supprimes :\n${impactText}\n` +
        `+ les logs OCPP/MQTT.\n\nACTION IRREVERSIBLE — l'historique de facturation sera perdu. Continuer ?`
    );
    if (!ok) return;
    const typed = window.prompt(
      `Confirmation renforcee — tapez le nom EXACT de la borne :\n${label}`
    );
    if (typed !== label) {
      setToast({ kind: 'err', msg: 'Nom incorrect : suppression annulee.' });
      return;
    }
    setBusy(true);
    try {
      await forceDeleteDevice(sn, env);
      navigate('/inventaire');
    } catch (e) {
      setToast({ kind: 'err', msg: (e as Error).message });
      setBusy(false);
    }
  }

  async function doDelete() {
    const label = device?.deviceName || sn;
    if (
      !window.confirm(
        `Supprimer definitivement "${label}" en base ? Irreversible (refuse si historique).`
      )
    )
      return;
    setBusy(true);
    setToast(null);
    try {
      await deleteDevice(sn, env);
      navigate('/inventaire');
    } catch (e) {
      setBusy(false);
      if ((e as ApiError).status === 409) await forceDelete(label);
      else setToast({ kind: 'err', msg: (e as Error).message });
    }
  }

  const hasGps = isValidLatLng(device.latitude, device.longitude);
  const points: MapPoint[] =
    device.deviceSn && hasGps
      ? [
          {
            sn: device.deviceSn,
            name: device.deviceName,
            lat: device.latitude as number,
            lng: device.longitude as number,
          },
        ]
      : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/inventaire" className="text-[13px] t-link hover:underline">
          &larr; Vue generale
        </Link>
        <h1 className="page-title">{device.deviceName || device.deviceSn}</h1>
        <span className={env === 'prod' ? 'pill-env-prod' : 'pill-env-staging'}>
          <span className="pill-dot" />
          {env}
        </span>
        <LifecycleBadge status={device.lifecycleStatus} />
      </div>

      {toast && (
        <div
          className={`text-[13px] rounded-md border px-3 py-2 ${
            toast.kind === 'ok' ? 'note-green' : 'note-red'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* The 8 tracked metrics (S/N ZCE + Nom de client saisis backoffice, vides par defaut) */}
      <div className="stat-strip sm:grid-cols-2 xl:grid-cols-4">
        <Field label="ZCE Serial Number (S/N)" value={device.zceSerialNumber} />
        <Field label="Nom de client" value={device.clientName} />
        <Field label="Date de fabrication" value={device.manufacturingDate} />
        <Field label="Version hardware" value={device.hardwareVersion} />
        <Field label="Version firmware" value={device.firmwareVersion} />
        <Field
          label="Station actuelle"
          value={
            device.placeName ||
            device.placeAddress ||
            (device.installationName
              ? `${device.installationName}${device.zoneName ? ` › ${device.zoneName}` : ''}`
              : null) ||
            (hasGps ? `${device.latitude}, ${device.longitude}` : null)
          }
        />
        {isMqtt ? (
          <Field
            label="Organisation"
            value={
              device.orgName
                ? `${device.orgName}${device.orgAddedBy ? ` (${device.orgAddedBy})` : ''}`
                : null
            }
          />
        ) : (
          <Field label="Client" value={device.ownerName || device.ownerEmail || device.orgName} />
        )}
        <Field
          label="Prix"
          value={device.devicePrice != null ? `${device.devicePrice.toFixed(2)} EUR` : null}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="panel">
          <div className="panel-header">
            <h2 className="panel-title">Informations</h2>
          </div>
          <div className="px-5 py-2">
            <Row k="Environnement" v={env} />
            <Row k="N. de serie" v={device.deviceSn} mono />
            <div className="dl-row">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] t-muted">
                Protocole
              </span>
              <span className="justify-self-end">
                <ProtocolBadge protocol={device.protocol} />
              </span>
            </div>
            <Row k="Type" v={device.deviceType} />
            <Row k="Statut" v={device.deviceStatus} />
            <Row
              k="En ligne"
              v={
                device.online === 1
                  ? device.mqttOnline === 1 && device.ocppOnline === 1
                    ? 'Oui (MQTT + OCPP)'
                    : device.ocppOnline === 1
                      ? 'Oui (OCPP)'
                      : 'Oui (MQTT)'
                  : 'Non'
              }
            />
            <Row
              k="Relais"
              v={device.relayState === 1 ? 'ON' : device.relayState === 0 ? 'OFF' : null}
            />
            <Row
              k="Vendor / Modele"
              v={[device.vendor, device.model].filter(Boolean).join(' / ') || null}
            />
            <Row k="Derniere connexion" v={device.lastBoot ?? device.mqttLastSeen} />
            {isMqtt && device.dashboardName && <Row k="Nom dashboard" v={device.dashboardName} />}
            <Row k="Cree le" v={device.createdAt} />
          </div>
        </div>

        <div className="panel p-0 map-frame">
          {points.length ? (
            <DeviceMap points={points} height={280} />
          ) : (
            <p className="p-5 text-[13px] t-muted">Pas de coordonnees GPS.</p>
          )}
        </div>
      </div>

      {/* Remote maintenance (operator/admin only), routed to this device's environment */}
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            Maintenance a distance{' '}
            <span className="text-xs t-muted font-normal font-sans">({env})</span>
          </h2>
        </div>
        <div className="p-5">
          {!canMaintain ? (
            <p className="text-[13px] t-muted">
              Role operator/admin requis pour piloter cet appareil.
            </p>
          ) : (
            <div className="space-y-4">
              {!isMqtt && (
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => ocppCommand(env, sn, 'reset', { type: 'Soft' }),
                        'Commande Reset envoyee'
                      )
                    }
                    className="btn-outline"
                  >
                    Reset (Soft)
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(
                        () => ocppCommand(env, sn, 'unlock_connector', { connectorId: 1 }),
                        'Commande Unlock envoyee'
                      )
                    }
                    className="btn-outline"
                  >
                    Unlock connecteur
                  </button>
                  <button
                    disabled={busy}
                    onClick={() =>
                      run(
                        () =>
                          ocppCommand(env, sn, 'trigger_message', {
                            requestedMessage: 'StatusNotification',
                          }),
                        'Trigger envoye'
                      )
                    }
                    className="btn-outline"
                  >
                    Trigger status
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <button
                  disabled={busy}
                  onClick={() => run(() => setRelay(env, sn, 1), 'Relais ON envoye')}
                  className="btn-outline"
                >
                  Relais ON
                </button>
                <button
                  disabled={busy}
                  onClick={() => run(() => setRelay(env, sn, 0), 'Relais OFF envoye')}
                  className="btn-outline"
                >
                  Relais OFF
                </button>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  value={otaUrl}
                  onChange={(e) => setOtaUrl(e.target.value)}
                  placeholder="URL firmware (optionnel)"
                  className="input flex-1 min-w-[220px]"
                />
                <button
                  disabled={busy}
                  onClick={() =>
                    run(() => triggerOta(env, sn, otaUrl || undefined), 'OTA declenche')
                  }
                  className="btn-outline"
                >
                  Declencher OTA
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cycle de vie : etat courant, transition (editeur+), fiche de vie */}
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            Cycle de vie <span className="text-xs t-muted font-normal font-sans">({env})</span>
          </h2>
        </div>
        <div className="p-5 space-y-4">
          {!lifecycle || !lifecycle.available ? (
            <p className="text-[13px] t-muted">
              Suivi du cycle de vie non disponible sur cet environnement (migration{' '}
              {isMqtt ? '012 (mqtt_device_states)' : '002'} non appliquee).
            </p>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="label inline mb-0">Etape actuelle :</span>
                <LifecycleBadge status={device.lifecycleStatus} />
                {device.lifecycleUpdatedAt && (
                  <span className="text-xs t-muted num">depuis le {device.lifecycleUpdatedAt}</span>
                )}
              </div>

              {canMaintain && writesOn && (device.lifecycleStatus || isMqtt) && (
                <div className="flex flex-wrap gap-2 items-end border-t bd pt-3">
                  <div>
                    <label className="label">Nouvelle etape</label>
                    <select
                      value={lcTarget}
                      onChange={(e) => setLcTarget(e.target.value as LifecycleStatus | '')}
                      className="input w-auto min-w-[180px]"
                    >
                      <option value="">Choisir...</option>
                      {/* Premiere etape d'un appareil MQTT : libre ; ensuite la matrice, comme l'OCPP. */}
                      {(device.lifecycleStatus
                        ? (lifecycle.transitions[device.lifecycleStatus as LifecycleStatus] ?? [])
                        : LIFECYCLE_ORDER
                      ).map((s) => (
                        <option key={s} value={s}>
                          {LIFECYCLE_META[s].label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <label className="label">Motif (recommande)</label>
                    <input
                      value={lcReason}
                      onChange={(e) => setLcReason(e.target.value)}
                      maxLength={255}
                      placeholder="ex : pose chez le client, panne compteur, ticket SAV..."
                      className="input"
                    />
                  </div>
                  <button disabled={busy || !lcTarget} onClick={doTransition} className="btn">
                    Changer d'etape
                  </button>
                  <span className="basis-full text-xs t-muted">
                    Seules les etapes autorisees depuis l'etape actuelle sont proposees. Chaque
                    changement est journalise (qui, quand, pourquoi).
                  </span>
                </div>
              )}
              {canMaintain && !writesOn && (
                <p className="text-[13px] rounded-md border note-amber px-3 py-2">
                  Lecture seule : l'ecriture n'est pas activee sur cet environnement.
                </p>
              )}

              <div className="space-y-3">
                <h3 className="eyebrow">Fiche de vie</h3>
                {lifecycle.events.length === 0 ? (
                  <p className="text-[13px] t-muted">Aucun evenement.</p>
                ) : (
                  <ol className="space-y-0">
                    {lifecycle.events.map((ev, i) => (
                      <li key={ev.id} className="timeline-item">
                        {i < lifecycle.events.length - 1 && (
                          <span className="timeline-line" aria-hidden="true" />
                        )}
                        <span
                          className={i === 0 ? 'timeline-dot-current' : 'timeline-dot'}
                          aria-hidden="true"
                        />
                        <div className="text-xs t-muted num">
                          {ev.createdAt}
                          {ev.actorEmail
                            ? ` · ${ev.actorEmail}`
                            : ev.source === 'backfill'
                              ? ' · reprise initiale'
                              : ''}
                        </div>
                        <div className="text-sm flex items-center gap-2 flex-wrap">
                          {ev.fromStatus ? (
                            <>
                              <LifecycleBadge status={ev.fromStatus} />
                              <span className="t-muted">&rarr;</span>
                            </>
                          ) : null}
                          <LifecycleBadge status={ev.toStatus} />
                        </div>
                        {ev.reason && (
                          <div className="text-[13px] t-muted italic">
                            &laquo; {ev.reason} &raquo;
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edition (editeur+) */}
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            Edition <span className="text-xs t-muted font-normal font-sans">({env})</span>
          </h2>
        </div>
        <div className="p-5">
          {!canMaintain && (
            <p className="text-[13px] t-muted">Role editeur (operator) ou admin requis.</p>
          )}
          {canMaintain && !writesOn && (
            <p className="text-[13px] rounded-md border note-amber px-3 py-2">
              Ecriture non activee sur cet environnement.
            </p>
          )}
          {canMaintain && writesOn && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="label">Nom</span>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={255}
                    className="input"
                  />
                </label>
                <label className="block">
                  <span className="label">Date de fabrication</span>
                  <input
                    type="date"
                    value={editMfg}
                    onChange={(e) => setEditMfg(e.target.value)}
                    className="input"
                  />
                </label>
                <label className="block">
                  <span className="label">Version hardware</span>
                  <input
                    value={editHw}
                    onChange={(e) => setEditHw(e.target.value)}
                    maxLength={50}
                    className="input"
                  />
                </label>
                <label className="block">
                  <span className="label">ZCE Serial Number (S/N)</span>
                  <input
                    value={editZceSn}
                    onChange={(e) => setEditZceSn(e.target.value)}
                    maxLength={100}
                    placeholder="vide par defaut — ex : ZCE-2026-00042"
                    className="input"
                  />
                </label>
                <label className="block">
                  <span className="label">Nom de client</span>
                  <input
                    value={editClientName}
                    onChange={(e) => setEditClientName(e.target.value)}
                    maxLength={255}
                    placeholder="vide par defaut — nom du client / societe"
                    className="input"
                  />
                </label>
                {isMqtt ? (
                  <>
                    <label className="block">
                      <span className="label">Type</span>
                      <select
                        value={editType}
                        onChange={(e) => setEditType(e.target.value)}
                        className="input"
                      >
                        <option value="">- aucun -</option>
                        {types.map((t) => (
                          <option key={t.typeId} value={t.typeId}>
                            {t.typeName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="block">
                      <span className="label">Organisation (dashboard)</span>
                      <div
                        className="input flex items-center t-body"
                        title="Revendication dans le dashboard (org_bornes / device_claims), en lecture"
                      >
                        {device.orgName || '- aucune -'}
                        {device.orgAddedBy ? (
                          <span className="ml-2 text-xs t-faint">
                            ajoute par {device.orgAddedBy}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <label className="block">
                    <span className="label">Client (proprietaire)</span>
                    <select
                      value={editOwner}
                      onChange={(e) => setEditOwner(e.target.value)}
                      className="input"
                    >
                      {!editOwner && <option value="">- aucun (stock) -</option>}
                      {device.ownerUuid &&
                        !clients.some((c) => c.clientUuid === device.ownerUuid) && (
                          <option value={device.ownerUuid}>
                            {device.ownerName || device.ownerEmail || 'Client actuel'}
                          </option>
                        )}
                      {clients.map((c) => (
                        <option key={c.clientUuid} value={c.clientUuid}>
                          {c.displayName}
                          {c.email && c.displayName !== c.email ? ` (${c.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button disabled={busy} onClick={saveEdit} className="btn">
                  Enregistrer
                </button>
                <span className="text-xs t-muted">
                  {isMqtt
                    ? 'Champs portes par mqtt_device_states (migration 013). Le « Client » d’un appareil MQTT est l’organisation qui l’a revendique dans le dashboard : il se change la-bas.'
                    : 'Seuls ces 6 champs sont modifiables — les autres sont geres par wattelzce / place-service. S/N ZCE et Nom de client sont des libelles libres. Client = proprietaire reel de la borne dans l’app (transfert, demande confirmation).'}
                </span>
              </div>

              {isMqtt ? (
                <p className="border-t bd pt-4 text-xs t-muted">
                  Suppression d&apos;un appareil MQTT : depuis Audit &rsaquo; Connectivite &rsaquo;
                  Nettoyer (avec corbeille et restauration).
                </p>
              ) : (
                <div className="border-t bd pt-4 flex flex-wrap gap-2 items-center">
                  <button disabled={busy} onClick={doRetire} className="btn-outline">
                    Retirer du service (inactive)
                  </button>
                  {isAdmin && (
                    <button disabled={busy} onClick={doDelete} className="btn-danger">
                      Supprimer definitivement
                    </button>
                  )}
                  <span className="text-xs t-muted">
                    Retirer = statut « inactive » (historique conserve). Suppression = DELETE en
                    base, refusee si la borne a un historique (admin uniquement).
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Firmware history */}
      <div className="panel p-0">
        <div className="panel-header">
          <h2 className="panel-title">Historique firmware</h2>
        </div>
        {device.firmwareHistory.length === 0 ? (
          <p className="empty-state p-8 text-sm t-muted">Aucun.</p>
        ) : (
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="th">Date</th>
                  <th className="th">Statut</th>
                  <th className="th">URL</th>
                </tr>
              </thead>
              <tbody>
                {device.firmwareHistory.map((f) => (
                  <tr key={f.id} className="tr-hover">
                    <td className="td num">{f.updateDate}</td>
                    <td className="td">{f.status}</td>
                    <td className="td truncate max-w-[320px]">
                      <a
                        href={f.firmwareUrl}
                        className="t-link hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {f.firmwareUrl}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="stat-cell">
      <div className="kpi-label">{label}</div>
      <div className="font-heading text-lg font-semibold t-strong tracking-tight break-words num">
        {value ?? '-'}
      </div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string | number | null | undefined; mono?: boolean }) {
  return (
    <div className="dl-row">
      <span className="text-[11px] font-semibold uppercase tracking-[0.06em] t-muted">{k}</span>
      {mono ? (
        <span className="kbd-id justify-self-end break-all">{v ?? '-'}</span>
      ) : (
        <span className="text-[13px] t-strong text-right justify-self-end break-all num">
          {v ?? '-'}
        </span>
      )}
    </div>
  );
}
